import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { CheckCircle, XCircle, Clock, Tag, X } from 'lucide-react';

const AdminTimeEntries = () => {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkTagId, setBulkTagId] = useState('');
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    user_id: '',
    project_id: '',
  });

  const fetchEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.project_id) params.append('project_id', filters.project_id);
      const res = await api.get(`/time-entries/all?${params}`);
      setEntries(res.data);
    } catch (error) {
      console.error('Fehler:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await api.get('/tags');
      setAllTags(res.data);
    } catch (error) {
      console.error('Tags-Fehler:', error);
    }
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/users');
        setUsers(res.data);
      } catch (error) {
        console.error('Fehler:', error);
      }
    };
    const fetchProjects = async () => {
      try {
        const res = await api.get('/projects');
        setProjects(res.data);
      } catch (error) {
        console.error('Projekte-Fehler:', error);
      }
    };
    fetchUsers();
    fetchProjects();
    fetchTags();
  }, [fetchTags]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/time-entries/${id}/status`, { status });
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Statuswechsel.');
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkTagId || selectedIds.length === 0) return;
    try {
      await api.post('/tags/bulk-assign', { time_entry_ids: selectedIds, tag_ids: [bulkTagId] });
      setSelectedIds([]);
      setBulkTagId('');
      fetchEntries();
    } catch (error) {
      alert('Fehler beim Zuweisen der Tags.');
    }
  };

  const handleBulkRemove = async () => {
    if (!bulkTagId || selectedIds.length === 0) return;
    try {
      await api.post('/tags/bulk-remove', { time_entry_ids: selectedIds, tag_ids: [bulkTagId] });
      setSelectedIds([]);
      setBulkTagId('');
      fetchEntries();
    } catch (error) {
      alert('Fehler beim Entfernen der Tags.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === entries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(entries.map(e => e.id));
    }
  };

  const calcHours = (start, end, breakMin) => {
    if (!start || !end) return '-';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const total = (eh * 60 + em - sh * 60 - sm - (breakMin || 0)) / 60;
    return total > 0 ? total.toFixed(2) : '0.00';
  };

  const totalHours = entries.reduce((sum, e) => {
    const h = parseFloat(calcHours(e.start_time, e.end_time, e.break_minutes));
    return sum + (isNaN(h) ? 0 : h);
  }, 0);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Laden...</div>;

  return (
    <div>
      <div className="page-header">
        <h2>Alle Zeiteinträge</h2>
        <p>Übersicht und Genehmigung aller Mitarbeiter-Zeiteinträge</p>
      </div>

      <div className="filters">
        <div className="form-group">
          <label>Von</label>
          <input type="date" className="form-control" value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Bis</label>
          <input type="date" className="form-control" value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Mitarbeiter</label>
          <select className="form-control" value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}>
            <option value="">Alle</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Projekt</label>
          <select className="form-control" value={filters.project_id}
            onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}>
            <option value="">Alle</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-label">Einträge</div>
          <div className="stat-value">{entries.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Gesamtstunden</div>
          <div className="stat-value">{totalHours.toFixed(1)}</div>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-tag-bar">
          <span className="bulk-count">{selectedIds.length} ausgewählt</span>
          <select value={bulkTagId} onChange={(e) => setBulkTagId(e.target.value)}>
            <option value="">Tag wählen...</option>
            {allTags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn btn-success btn-sm" onClick={handleBulkAssign} disabled={!bulkTagId}>
            <Tag size={12} /> Zuweisen
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleBulkRemove} disabled={!bulkTagId}>
            <X size={12} /> Entfernen
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds([])}>
            Auswahl aufheben
          </button>
        </div>
      )}

      <div className="card">
        <div className="table-container">
          {entries.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}><input type="checkbox" className="row-checkbox" onChange={toggleSelectAll} checked={selectedIds.length === entries.length && entries.length > 0} /></th>
                  <th>Mitarbeiter</th>
                  <th>Datum</th>
                  <th>Start</th>
                  <th>Ende</th>
                  <th>Pause</th>
                  <th>Stunden</th>
                  <th>Projekt</th>
                  <th>Beschreibung</th>
                  <th>Tags</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} style={selectedIds.includes(entry.id) ? { background: '#f0f4ff' } : {}}>
                    <td><input type="checkbox" className="row-checkbox" checked={selectedIds.includes(entry.id)} onChange={() => toggleSelect(entry.id)} /></td>
                    <td><strong>{entry.first_name} {entry.last_name}</strong></td>
                    <td>{new Date(entry.date).toLocaleDateString('de-DE')}</td>
                    <td>{entry.start_time?.substring(0, 5)}</td>
                    <td>{entry.end_time?.substring(0, 5)}</td>
                    <td>{entry.break_minutes} Min</td>
                    <td><strong>{calcHours(entry.start_time, entry.end_time, entry.break_minutes)}</strong></td>
                    <td>{entry.project_name || '-'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.description || '-'}
                    </td>
                    <td>
                      <div className="tags-cell">
                        {(entry.tags || []).map(tag => (
                          <span key={tag.id} className="tag" style={{ background: tag.color + '20', color: tag.color }}>
                            <span className="tag-dot" style={{ background: tag.color }} />
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td><span className={`badge badge-${entry.status}`}>{entry.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {entry.status === 'submitted' && (
                          <>
                            <button className="btn btn-success btn-sm btn-icon"
                              onClick={() => handleStatusChange(entry.id, 'approved')} title="Genehmigen">
                              <CheckCircle size={14} />
                            </button>
                            <button className="btn btn-danger btn-sm btn-icon"
                              onClick={() => handleStatusChange(entry.id, 'rejected')} title="Ablehnen">
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <Clock size={40} />
              <p>Keine Zeiteinträge im gewählten Zeitraum.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTimeEntries;
